import { wei } from '@kwenta/wei'
import CloseIcon from 'assets/svg/app/close.svg'
import SnxLogo from 'assets/svg/partners/snx.svg'
import Button from 'components/Button/Button'
import { Checkbox } from 'components/Checkbox'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { providerChainIcon } from 'constants/network'
import Image from 'next/image'

import { type FC, memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SectionHeading } from 'sections/shared/components/SectionHeading'
import { setOpenModal } from 'state/app/reducer'
import { selectShowModalWithParams } from 'state/app/selectors'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { withdrawIsolatedMargin } from 'state/futures/isolatedMargin/actions'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import styled from 'styled-components'
import { resetButtonCSS } from 'styles/common'
import { SYNTH_ICONS } from 'utils/icons'

interface WithdrawConfirmationPanelProps {
	onDismiss(): void
}

interface WithdrawConfirmationPanelParams {
	amount: string
	withdrawAddress: string
	selectedChain: string
	selectedCoin: string
}
const WithdrawConfirmationPanel: FC<WithdrawConfirmationPanelProps> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const params = useAppSelector(selectShowModalWithParams)?.params
	const { amount, withdrawAddress, selectedChain, selectedCoin } =
		params as WithdrawConfirmationPanelParams
	const [verificationChecked, setVerificationChecked] = useState(false)
	const showWalletDrawer = useCallback(() => dispatch(setOpenModal('manage_wallet')), [dispatch])
	const handleWithdraw = useCallback(() => {
		dispatch(withdrawIsolatedMargin(wei(amount)))
	}, [dispatch, amount])

	return (
		<Container>
			<FlexDivCol rowGap="20px" style={{ padding: '20px' }}>
				<FlexDivRowCentered>
					<SectionHeading title={t('account.withdraw-section.title')} marginTop={0} />
					<DismissButton onClick={onDismiss}>
						<CloseIcon width={12} height={12} />
					</DismissButton>
				</FlexDivRowCentered>
				<Spacer height={10} />
				<FlexDivCol rowGap="20px">
					<FlexDivCol rowGap="10px">
						<FlexDivRowCentered>
							<Body color="secondary">Address:</Body>
							<MultilineBody color="secondary" mono>
								{withdrawAddress}
							</MultilineBody>
						</FlexDivRowCentered>
						<FlexDivRow>
							<Body color="secondary">Network:</Body>
							<FlexDivRowCentered columnGap="4px">
								{providerChainIcon(perpsProvider, { height: 16, width: 16 })}
								<Body color="primary" weight="semiBold">
									{selectedChain}
								</Body>
							</FlexDivRowCentered>
						</FlexDivRow>
					</FlexDivCol>
					<Divider height={1} divider />
					<FlexDivCol rowGap="8px">
						<FlexDivRow>
							<Body color="secondary">Coin:</Body>
							<FlexDivRowCentered columnGap="4px">
								<Image
									src={SYNTH_ICONS[selectedCoin]}
									className={`${'synth-icon'}`}
									height={14}
									width={14}
									alt="USDC"
								/>
								<Body color="primary" weight="semiBold">
									{selectedCoin}
								</Body>
							</FlexDivRowCentered>
						</FlexDivRow>
						<FlexDivRow>
							<Body color="secondary">Amount:</Body>
							<Body color="primary" weight="semiBold" mono>
								{amount} {selectedCoin}
							</Body>
						</FlexDivRow>
						<FlexDivRow>
							<Body color="secondary">Network Fee:</Body>
							<FlexDivRow columnGap="4px">
								<Body color="secondary" mono>
									0.0010 USDC
								</Body>
							</FlexDivRow>
						</FlexDivRow>
						<FlexDivRow>
							<Body color="secondary">Withdraw Account:</Body>
							<FlexDivRow columnGap="4px">
								<FlexDivRowCentered columnGap="5px">
									<SnxLogo height={14} width={14} />
									<Body weight="bold">Synthetix</Body>
								</FlexDivRowCentered>
							</FlexDivRow>
						</FlexDivRow>
					</FlexDivCol>
					<Divider height={1} divider />
					<ItemContainer columnGap="6px">
						<Checkbox
							id="country-check-box"
							label=""
							checked={verificationChecked}
							onChange={() => setVerificationChecked(!verificationChecked)}
							variant="fill"
							checkSide="left"
						/>
						<Body color="secondary">{t('account.withdrawal-confirm.no-verificaiton')}</Body>
					</ItemContainer>
					<Divider height={1} divider />
					<MessageContainer rowGap="4px">
						<Body color="secondary">{t('account.withdrawal-confirm.copy-1')}</Body>
						<Body color="secondary">{t('account.withdrawal-confirm.copy-2')}</Body>
					</MessageContainer>
				</FlexDivCol>
			</FlexDivCol>
			<Footer>
				<Button fullWidth onClick={showWalletDrawer} size="small">
					{t('account.withdrawal-confirm.back')}
				</Button>
				<Button fullWidth variant="solid-highlight" onClick={handleWithdraw} size="small">
					{t('account.withdrawal-confirm.submit')}
				</Button>
			</Footer>
		</Container>
	)
})

const Container = styled(FlexDivCol)`
	position: relative;
	height: 100%;
	min-height: 400px;
	overflow-y: scroll;
`

const MultilineBody = styled(Body)`
	overflow-wrap: break-word;
	text-align: right;
	width: 50%;
`
const Footer = styled(FlexDivRowCentered)`
	position: fixed;
	bottom: 0;
	width: 100%;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding: 15px;
	column-gap: 15px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: absolute;
	top: 25px;
	right: 20px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const ItemContainer = styled(FlexDivRow)`
	justify-content: flex-start;
`

const Divider = styled(Spacer)`
	width: 100%;
`

const MessageContainer = styled(FlexDivCol)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
	padding: 10px;
	border: 1px solid rgba(239, 104, 104, 0);
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.pill.yellowGray.background};
	border-radius: 8px;
	cursor: default;
	white-space: pre-line;
`

export default WithdrawConfirmationPanel
